import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";

export const useFetchDivision = ({ divisionId, ...params }) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/divisions/${divisionId}`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["divisions", organizationId, divisionId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
    enabled: !!divisionId,
  });
};

export const useFetchDivisions = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, conferenceId } = useParams();
  let URL = `${sport}/o/${organizationId}/conferences/${conferenceId}/divisions`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["divisions", organizationId, conferenceId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useDeleteDivision = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (divisionId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/divisions/${divisionId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "division.delete.success",
          defaultMessage: "Division deleted successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["divisions"] });
    },
  });
};

export const useCreateDivision = () => {
  const { sport, organizationId, conferenceId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/conferences/${conferenceId}/divisions`,
        method: "POST",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "division.create.success",
          defaultMessage: "Division created successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["divisions"] });
    },
  });
};

export const useUpdateDivision = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({ divisionId, data }) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/divisions/${divisionId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "division.update.success",
          defaultMessage: "Division updated successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["divisions"] });
    },
  });
};
